import type { ConfigurationValidationError } from '@festina/configuration-general-types/configuration'
import { ConfigurationRepositoryBranchesService } from '@festina/configuration-general-types/configuration/services/ConfigurationRepositoryBranchesService'
import {
  assertNotEmpty,
  type ConfigurableServer,
  type Except,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

import { fetchBranchErrorsOnLayersKey } from '../keys/configuration'
import { STANDARD_BRANCH } from './BranchNames'
import { fetchAndDeduplicateErrors } from './errors/helpers'
import type { LayerKey } from './layers/LayerKey'
import type { Tree } from './types'

type CommonProps = {
  server: Optional<ConfigurableServer>
  branchName: Optional<string>
  enabled?: boolean
}

type FetchBranchErrorProps = CommonProps & {
  layer: LayerKey
  block: string
}

type FetchAllLayerErrorsProps = Except<
  FetchBranchErrorProps,
  'layer' | 'block'
> & {
  layers: LayerKey[]
  blocks: string[]
  layerTopology: Optional<Tree>
  blockTopology: Optional<Tree>
}

const isEnabled = (props: CommonProps) =>
  props.enabled !== false &&
  !!props.server &&
  !!props.branchName &&
  props.branchName !== STANDARD_BRANCH

export const useFetchBranchErrorsOnAllBlocksAndLayers = (
  props: FetchAllLayerErrorsProps,
) => {
  const fetcher = useCallback(
    async ({
      layer,
      block,
    }: {
      layer: LayerKey
      block: string
    }): Promise<ConfigurationValidationError[] | null> => {
      const { server, branchName } = props

      assertNotEmpty(server)
      assertNotEmpty(branchName)

      const result = await (layer?.layerId
        ? ConfigurationRepositoryBranchesService.branchServiceGetGetBranchLayerErrors(
            {
              componentName: server.toLocaleLowerCase(),
              layer: layer.layerId,
              block,
              tenantId: layer.tenantId ?? undefined,
              branchName,
            },
          )
        : ConfigurationRepositoryBranchesService.branchServiceGetGetBranchErrors(
            {
              componentName: server.toLocaleLowerCase(),
              tenantId: layer.tenantId ?? undefined,
              branchName,
              block,
            },
          ))

      return (result?.errors ?? []).filter(notEmpty)
    },
    [props],
  )

  // We intentionally use useQuery in order to avoid suspense from kicking in inside the branches panel,
  // making it snappier for users to click through branch in the branches tree.
  return useQuery({
    enabled: isEnabled(props),
    queryKey:
      props.branchName && props.server
        ? fetchBranchErrorsOnLayersKey({
            branchName: props.branchName,
            server: props.server,
            layers: props.layers,
          })
        : [],
    queryFn: () =>
      fetchAndDeduplicateErrors({
        blocks: props.blocks,
        layers: props.layers,
        fetcher,
        layerTopology: props.layerTopology,
        blockTopology: props.blockTopology,
      }),
    retry: false,
    staleTime: Infinity,
  })
}
